import './App.css';
import './studio.css';
import { useEffect, useMemo, useRef, useState } from 'react';
import JSZip from 'jszip';

import { DEFAULTS, PLATFORM_PRESETS, SETTINGS_SCHEMA } from './imagegen/schema';
import { buildPrompt } from './imagegen/promptBuilder';
import {
  buildPollinationsAudioUrl,
  buildPollinationsImageUrl,
  buildPollinationsTextUrl,
  buildPollinationsVideoUrl,
  downloadImageFromUrl,
} from './imagegen/pollinationsClient';
import { clearHistory, loadHistory, saveHistory } from './imagegen/storage';
import { loadActiveBrandId, loadBrands, saveActiveBrandId, saveBrands } from './imagegen/brandStorage';
import { loadCloudState, saveCloudState } from './imagegen/cloudState';
import { makeDefaultBrandProfile, makeScanDineExampleBrand } from './imagegen/brandDefaults';
import { extractPaletteFromImageData, imageDataToPngDataUrl, loadImageToImageData } from './imagegen/colorUtils';
import { analyzeReferenceImage } from './imagegen/referenceUtils';
import {findModel,resolveGenerationRequest} from './imagegen/generationRequest';
import {quoteGeneration,quoteLabel} from './imagegen/pricing';
import {generateImage} from './imagegen/generationClient';
import {inspectImage,MAX_REFERENCE_BYTES} from './imagegen/referenceValidation';
import {savedResultUrl,sanitizeHistoryItem} from './imagegen/resultStorage';
import ImageRequestPanel from './ui/ImageRequestPanel';
import { downloadImageWithLogoOverlay } from './imagegen/logoOverlay';
import {
  fetchPollinationsAudioModels,
  fetchPollinationsImageModels,
  fetchPollinationsTextModels,
  loadCachedModels,
  saveCachedModels,
} from './imagegen/modelsApi';
import { clearRuntimeApiKey, loadRuntimeApiKey, saveRuntimeApiKey } from './imagegen/apiKeyStorage';
import { audioExtension, modelsForAudioOperation } from './imagegen/modelCatalog';

import useIsMobile from './ui/useIsMobile';
import Sheet from './ui/Sheet';
import ApiKeyDialog from './ui/ApiKeyDialog';
import PreviewHero from './ui/PreviewHero';
import TabsPanel from './ui/TabsPanel';
import PromptCard from './ui/PromptCard';
import StudioIcon from './ui/StudioIcon';
import BrandKitPage from './ui/BrandKitPage';
import LibraryPage from './ui/LibraryPage';
import { SettingsForm } from './ui/FormFields';
import { useI18n } from './i18n/I18nProvider';

function clampNumber(value, { min, max }) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(max, Math.max(min, n));
}

function normalizeSettings(settings) {
  const width = clampNumber(settings.width, { min: 256, max: 2048 });
  const height = clampNumber(settings.height, { min: 256, max: 2048 });
  const historyLimit = clampNumber(settings.historyLimit, { min: 6, max: 100 });

  const logoOverlaySizePct = clampNumber(settings.logoOverlaySizePct, { min: 5, max: 40 });
  const logoOverlayOpacity = clampNumber(settings.logoOverlayOpacity, { min: 0.05, max: 1 });
  const logoOverlayPadding = clampNumber(settings.logoOverlayPadding, { min: 0, max: 80 });
  const pos = String(settings.logoOverlayPosition || 'bottom_right');
  const allowedPos = ['top_left', 'top_right', 'bottom_left', 'bottom_right', 'center'];
  const logoOverlayPosition = allowedPos.includes(pos) ? pos : 'bottom_right';

  return {
    ...settings,
    width,
    height,
    historyLimit,
    logoInScene: Boolean(settings.logoInScene),
    logoOverlayEnabled: Boolean(settings.logoOverlayEnabled),
    logoOverlaySizePct,
    logoOverlayOpacity,
    logoOverlayPadding,
    logoOverlayPosition,
    promptAssist: false,
  };
}

function nowIso() {
  return new Date().toISOString();
}

function makeId() {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}


function addKeyToUrl(urlStr, apiKey) {
  const key = String(apiKey || '').trim();
  if (!key || String(urlStr).startsWith('blob:')) return urlStr;
  try {
    const u = new URL(urlStr);
    u.searchParams.set('key', key);
    return u.toString();
  } catch {
    return urlStr;
  }
}

function resolveSeedToUse(settings) {
  if (settings.seedMode === 'custom') {
    const raw = String((settings.seedValue || '').trim());
    const n = Number(raw);
    if (!raw) return null;
    if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
      const err = new Error('CUSTOM_SEED_INVALID');
      err.code = 'CUSTOM_SEED_INVALID';
      throw err;
    }
    return String(n);
  }
  return String(Math.floor(Math.random() * 2147483647));
}

function estimateCreditsForRun({ pricing, mediaType, duration }) {
  const p = pricing || {};
  const mt = String(mediaType || '').trim();
  if (mt === 'image' && typeof p.completionImageTokens === 'number') return p.completionImageTokens;
  if (mt === 'video' && typeof p.completionVideoSeconds === 'number') {
    const seconds = clampNumber(duration, { min: 1, max: 60 });
    return p.completionVideoSeconds * seconds;
  }
  if (mt === 'audio' && typeof p.completionAudioSeconds === 'number') {
    return p.completionAudioSeconds * clampNumber(duration, { min: 1, max: 380 });
  }
  if (mt === 'audio' && typeof p.completionAudioTokens === 'number') return p.completionAudioTokens;
  return null;
}

function formatCredits(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '';
  if (Math.abs(v) >= 10) return String(Math.round(v * 10) / 10);
  return String(Math.round(v * 100) / 100);
}

export default function App() {
  const { locale, setLocale, t } = useI18n();
  const mobile = useIsMobile(1100);
  const companyUrl = 'https://codefalcon.me';
  const envApiKey = useMemo(() => String(process.env.REACT_APP_POLLINATIONS_API_KEY || '').trim(), []);
  const [theme, setTheme] = useState(() => {
    try {
      const saved = String(localStorage.getItem('brand_studio_theme_v1') || '').trim();
      if (saved === 'light' || saved === 'dark') return saved;
    } catch {
      // ignore
    }
    try {
      return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? 'dark' : 'light';
    } catch {
      return 'dark';
    }
  });
  const [runtimeApiKey, setRuntimeApiKey] = useState(() => loadRuntimeApiKey());
  const [apiKeyInput, setApiKeyInput] = useState(() => loadRuntimeApiKey());
  const [apiKeyDialogOpen, setApiKeyDialogOpen] = useState(false);
  const apiKey = String(runtimeApiKey || envApiKey || '').trim();

  const [settings, setSettings] = useState(() => normalizeSettings(DEFAULTS));
  const [error, setError] = useState('');
  const [generationNotice,setGenerationNotice]=useState('');
  const [needsReference,setNeedsReference]=useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [variations, setVariations] = useState([]); // [{id, seedUsed, sourceUrl}]
  const [variationStatus, setVariationStatus] = useState({}); // id -> loading|loaded|error
  const [previewLoadState, setPreviewLoadState] = useState({ status: 'idle', error: '' }); // idle|loading|loaded|error
  const isCreating = isGenerating || previewLoadState.status === 'loading';
  const [previewNonce, setPreviewNonce] = useState(0);
  const proxyFallbackTriedRef = useRef(new Set());

  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [brands, setBrands] = useState([]);
  const [activeBrandId, setActiveBrandId] = useState('');
  const [persistenceReady, setPersistenceReady] = useState(false);

  const [activeTab, setActiveTab] = useState('history'); // prompt | history
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [libraryPending, setLibraryPending] = useState(null);
  const [activePage, setActivePage] = useState(() => {
    try {
      const v = String(localStorage.getItem('brand_studio_active_page_v1') || '').trim();
      if (v === 'brandKit' || v === 'studio' || v === 'library') return v;
    } catch {
      // ignore
    }
    return 'studio';
  });


  const [imageModels, setImageModels] = useState(() => loadCachedModels('image') || []);
  const [textModels, setTextModels] = useState(() => loadCachedModels('text') || []);
  const [audioModels, setAudioModels] = useState(() => loadCachedModels('audio') || []);
  const [modelsError, setModelsError] = useState('');
  const [referenceFile, setReferenceFile] = useState(null);
  const [referenceMetadata, setReferenceMetadata] = useState(null);
  const [referenceIntent, setReferenceIntent] = useState('identity');
  const imageRequestRef = useRef(null);
  const uploadSequence = useRef(0);
  useEffect(() => () => imageRequestRef.current?.abort(), []);
  const [audioFile, setAudioFile] = useState(null);

  useEffect(() => {
    setApiKeyInput(runtimeApiKey || '');
  }, [runtimeApiKey]);

  useEffect(() => {
    const next = theme === 'light' ? 'light' : 'dark';
    try {
      document.documentElement.dataset.theme = next;
    } catch {
      // ignore
    }
    try {
      localStorage.setItem('brand_studio_theme_v1', next);
    } catch {
      // ignore
    }
  }, [theme]);

  useEffect(() => {
    try {
      localStorage.setItem('brand_studio_active_page_v1', activePage);
    } catch {
      // ignore
    }
  }, [activePage]);

  const apiConnected = Boolean(settings.useProxy || apiKey);
  const apiStatusText = settings.useProxy
    ? t('settings.apiKeyStatusProxy')
    : runtimeApiKey
      ? t('settings.apiKeyStatusLocalStorage')
      : envApiKey
        ? t('settings.apiKeyStatusEnv')
        : t('settings.apiKeyDialogNotConnected');

  useEffect(() => {
    let cancelled = false;
    async function loadPersistedState() {
      const cloud = await loadCloudState();
      if (cancelled) return;

      if (cloud) {
        setHistory(Array.isArray(cloud.history) ? cloud.history : []);
        setBrands(Array.isArray(cloud.brands) ? cloud.brands : []);
        setActiveBrandId(String(cloud.activeBrandId || '').trim());
      } else {
        setHistory(loadHistory());
        setBrands(loadBrands());
        setActiveBrandId(loadActiveBrandId());
      }

      setPersistenceReady(true);
    }
    loadPersistedState();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSettingsOpen(false);
  }, [mobile]);

  useEffect(() => {
    if (activePage !== 'studio') {
      setSettingsOpen(false);
      return;
    }
  }, [activePage, mobile]);

  useEffect(() => {
    if (!persistenceReady) return;
    if (brands.length) return;
    const seeded = [makeScanDineExampleBrand()];
    setBrands(seeded);
    setActiveBrandId(seeded[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brands.length, persistenceReady]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setModelsError('');
      try {
        const results = await Promise.allSettled([
          fetchPollinationsImageModels(),
          fetchPollinationsTextModels(),
          fetchPollinationsAudioModels(),
        ]);
        if (cancelled) return;

        const [img, txt, aud] = results;
        if (img.status === 'fulfilled') {
          setImageModels(img.value);
          saveCachedModels('image', img.value);
        }
        if (txt.status === 'fulfilled') {
          setTextModels(txt.value);
          saveCachedModels('text', txt.value);
        }
        if (aud.status === 'fulfilled') {
          setAudioModels(aud.value);
          saveCachedModels('audio', aud.value);
        }

        const errs = results
          .filter((r) => r.status === 'rejected')
          .map((r) => String(r.reason || 'Failed to load models'));
        if (errs.length) setModelsError(errs.join(' • '));
      } catch (e) {
        if (cancelled) return;
        setModelsError(String(e));
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!persistenceReady) return;
    const serializableHistory = settings.persistHistory
      ? history.slice(0, settings.historyLimit).map(sanitizeHistoryItem)
      : [];

    const payload = {
      history: serializableHistory,
      brands,
      activeBrandId,
    };

    const timer = setTimeout(async () => {
      const ok = await saveCloudState(payload);
      if (ok) return;
      saveHistory(serializableHistory);
      saveBrands(brands);
      saveActiveBrandId(activeBrandId);
    }, 350);

    return () => clearTimeout(timer);
  }, [persistenceReady, settings.persistHistory, settings.historyLimit, history, brands, activeBrandId]);

  useEffect(() => {
    if (!settings.useBrandProfile) return;
    if (settings.brandId) return;
    if (!activeBrandId) return;
    setSettings((prev) => ({ ...prev, brandId: activeBrandId }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBrandId, settings.useBrandProfile]);

  const activeBrand = useMemo(() => {
    const id = settings.brandId || activeBrandId;
    return brands.find((b) => b.id === id) || null;
  }, [brands, settings.brandId, activeBrandId]);

  const promptPreview = useMemo(() => {
    const normalized = normalizeSettings(settings);
    const withBrand = { ...normalized, _brandProfile: activeBrand };
    if (normalized.mediaType === 'audio') {
      return { prompt: String(normalized.subject || ''), negative_prompt: '', width: normalized.width, height: normalized.height };
    }
    const { prompt, negative_prompt, width, height } = buildPrompt(withBrand);
    return { prompt, negative_prompt, width, height };
  }, [settings, activeBrand]);

  const previewUrl = savedResultUrl(current);

  useEffect(() => {
    const type = current?.mediaType || 'image';
    if (type === 'text') {
      setPreviewLoadState({ status: 'loaded', error: '' });
      return;
    }
    if (!previewUrl) {
      setPreviewLoadState({ status: 'idle', error: '' });
      return;
    }
    setPreviewLoadState({ status: 'loading', error: '' });
  }, [previewUrl, current?.mediaType, current?.id, previewNonce]);

  const selectedModelMeta = useMemo(() => {
    const type = settings.mediaType || 'image';
    const catalog = type === 'text' ? textModels : type === 'audio' ? audioModels : imageModels;
    return findModel(catalog,settings.model);
  }, [imageModels, textModels, audioModels, settings.mediaType, settings.model]);

  const inferredMediaType = settings.mediaType || 'image';

  const allowedModelsForType = useMemo(() => {
    const type = settings.mediaType || 'image';
    if (type === 'text') return (textModels || []).filter((m) => m && typeof m.name === 'string').map((m) => m.name);
    if (type === 'audio') return modelsForAudioOperation(audioModels, settings.audioOperation || 'speech').map((m) => m.name);

    const wantVideo = type === 'video';
    return (imageModels || [])
      .filter((m) => m && typeof m.name === 'string')
      .filter((m) => {
        const outs = m.output_modalities;
        const isVideo = Array.isArray(outs) && outs.includes('video');
        return wantVideo ? isVideo : !isVideo;
      })
      .map((m) => m.name);
  }, [imageModels, textModels, audioModels, settings.mediaType, settings.audioOperation]);

  useEffect(() => {
    // Resolve aliases only for images: never silently choose a different model.
    if (settings.mediaType === 'image') {
      const match = findModel(imageModels,settings.model);
      if (match && match.name !== settings.model) setSettings(prev => ({...prev,model:match.name}));
      return;
    }
    // Ensure selected model matches the chosen Type.
    if (!allowedModelsForType.length) return;
    if (allowedModelsForType.includes(settings.model)) return;

    const type = settings.mediaType || 'image';
    const preferred =
      (type === 'video'
        ? allowedModelsForType.find((m) => m === 'veo') || allowedModelsForType.find((m) => m === 'wan-fast')
        : type === 'text'
          ? allowedModelsForType.find((m) => m === 'openai-fast') || allowedModelsForType.find((m) => m === 'openai')
          : type === 'audio'
            ? allowedModelsForType.find((m) => m === 'elevenlabs') || allowedModelsForType[0]
            : allowedModelsForType.find((m) => m === 'flux')) || allowedModelsForType[0];
    if (!preferred) return;
    setSettings((prev) => ({ ...prev, model: preferred }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedModelsForType, settings.mediaType, settings.audioOperation]);

  useEffect(() => {
    if (settings.mediaType !== 'audio') return;
    const voices = selectedModelMeta?.voices;
    if (!Array.isArray(voices) || !voices.length) return;
    if (voices.includes(settings.voice)) return;
    setSettings((prev) => ({ ...prev, voice: voices[0] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.mediaType, selectedModelMeta, settings.voice]);

  const imageDraft = useMemo(() => ({prompt:settings.subject,modelId:settings.model,size:`${promptPreview.width}x${promptPreview.height}`,quality:settings.quality,outputCount:Number(settings.variations),referenceMetadata,referenceIntent,negativePrompt:settings.negativePromptExtra || ''}),[settings.subject,settings.model,settings.quality,settings.variations,settings.negativePromptExtra,promptPreview.width,promptPreview.height,referenceMetadata,referenceIntent]);
  const imageQuote = quoteGeneration(imageDraft,selectedModelMeta);
  let imageIssue = settings.mediaType === 'image' && needsReference ? (locale === 'ar' ? 'أعد إرفاق المرجع أو ابدأ بدون مرجع.' : 'Reattach the reference or explicitly start without it.') : '';
  if (settings.mediaType === 'image' && settings.subject.trim()) {
    try { resolveGenerationRequest(imageDraft,selectedModelMeta); } catch(e) { imageIssue=e.message; }
  }

  const modelCostLabel = useMemo(() => {
    const p = selectedModelMeta?.pricing || {};
    if (settings.mediaType === 'image') return quoteLabel(quoteGeneration({outputCount:Number(settings.variations)},selectedModelMeta),locale === 'ar');
    if (typeof p.completionImageTokens === 'number') return `${p.completionImageTokens} credits/img`;
    if (typeof p.completionVideoSeconds === 'number') return `${p.completionVideoSeconds} credits/sec`;
    if (typeof p.completionVideoTokens === 'number') return `${p.completionVideoTokens} credits/video-token`;
    if (typeof p.completionTextTokens === 'number') return `${p.completionTextTokens} credits/token`;
    if (typeof p.promptTextTokens === 'number') return `${p.promptTextTokens} credits/prompt-token`;
    if (typeof p.completionAudioTokens === 'number') return `${p.completionAudioTokens} credits/audio-token`;
    if (typeof p.completionAudioSeconds === 'number') return `${p.completionAudioSeconds} credits/sec-audio`;
    return '';
  }, [selectedModelMeta,settings.mediaType,settings.variations,locale]);

  const modelDailyEstimate = '';

  const modelRunEstimate = useMemo(() => {
    if (settings.mediaType === 'image') return '';
    const credits = estimateCreditsForRun({
      pricing: selectedModelMeta?.pricing,
      mediaType: settings.mediaType,
      duration: settings.duration,
    });
    if (!Number.isFinite(Number(credits))) return '';
    if (settings.mediaType === 'video') {
      const seconds = clampNumber(settings.duration, { min: 1, max: 60 });
      return `~${formatCredits(credits)} credits / ${seconds}s`;
    }
    if (settings.mediaType === 'image') return `~${formatCredits(credits)} credits / image`;
    return `~${formatCredits(credits)} credits`;
  }, [selectedModelMeta, settings.mediaType, settings.duration]);

  const totalEstimatedCredits = useMemo(() => {
    const sum = (history || []).reduce((acc, item) => {
      const v = Number(item?.estimatedCredits);
      if (!Number.isFinite(v)) return acc;
      return acc + v;
    }, 0);
    return Number.isFinite(sum) && sum > 0 ? sum : null;
  }, [history]);

  function updateSetting(key, value) {
    setSettings((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'mediaType') {
        if (value === 'video') {
          const videoNames = (imageModels || []).filter((m) => m?.output_modalities?.includes('video')).map((m) => m.name);
          next.model = videoNames.find((name) => name === 'veo') || videoNames.find((name) => name === 'wan-fast') || videoNames[0] || '';
          next.duration = next.duration || 8;
          next.aspectRatio = next.aspectRatio || '9:16';
        } else if (value === 'text') {
          next.model = 'openai-fast';
        } else if (value === 'audio') {
          next.model = 'elevenlabs';
          next.voice = next.voice || 'alloy';
        } else {
          next.model = 'z-image';
        }
      }
      if (key === 'audioOperation') {
        const candidates = modelsForAudioOperation(audioModels, value);
        next.model = candidates[0]?.name || '';
        next.subject = value === 'transcription' ? '' : next.subject;
      }
      if (key === 'platformPreset') {
        const preset = PLATFORM_PRESETS.find((p) => p.value === value);
        if (preset && preset.value !== 'custom') {
          next.width = preset.width;
          next.height = preset.height;
        }
      }
      return normalizeSettings(next);
    });
  }

  async function onUploadReference(file) {
    if (!file) return;
    const sequence=++uploadSequence.current;
    try {
      if (file.size > MAX_REFERENCE_BYTES) throw new Error('Reference image must be 10 MiB or smaller.');
      const info=inspectImage(new Uint8Array(await file.arrayBuffer()));
      if (info.type !== file.type) throw new Error('Choose a valid PNG, JPEG or WebP file.');
      const {dataUrl}=await analyzeReferenceImage(file);
      if(sequence!==uploadSequence.current)return;
      setNeedsReference(false);setReferenceFile(file);setReferenceMetadata({name:file.name,size:file.size,...info});
      setSettings(prev=>normalizeSettings({...prev,referenceImageDataUrl:dataUrl,referenceMode:'strong'}));setError('');
    } catch(e) { if(sequence===uploadSequence.current)setError(`Reference upload failed: ${e.message}`); }
  }
  function clearReference() {
    setNeedsReference(false);uploadSequence.current++;setReferenceFile(null);setReferenceMetadata(null);
    setSettings(prev=>normalizeSettings({...prev,referenceImageDataUrl:'',referenceMode:'off',referencePalette:[],referenceVibe:null}));
  }

  async function onGenerate() {
    if (isCreating || imageRequestRef.current) return;
    setError('');
    setGenerationNotice('');
    if(settings.mediaType === 'image' && needsReference) {setError('Reattach the reference before generating.');return;}
    setVariations([]);
    setVariationStatus({});

    const subject = String(settings.subject || '');
    if (!subject.trim() && !(settings.mediaType === 'audio' && settings.audioOperation === 'transcription')) {
      setError(t('validation.subjectRequired'));
      setSettingsOpen(false);
      return;
    }
    if (settings.mediaType !== 'image' && !apiKey && !settings.useProxy) {
      setError(t('settings.apiKeyHelp'));
      return;
    }

    setIsGenerating(true);
    setPreviewLoadState({ status: 'idle', error: '' });
    try {
      const normalized = normalizeSettings(settings);
      const withBrand = { ...normalized, _brandProfile: activeBrand };
      const { prompt, negative_prompt, width, height } = buildPrompt(withBrand);

      if (normalized.mediaType === 'image') {
        const request=resolveGenerationRequest(imageDraft,selectedModelMeta);
        const controller=new AbortController();imageRequestRef.current=controller;
        try {
          const response=await generateImage({...request,quote:imageQuote},referenceFile,controller.signal);
          if(controller.signal.aborted || imageRequestRef.current!==controller)return;
          const snapshot=response.requestSnapshot;
          const items=response.results.map(result=>({id:result.id || makeId(),createdAt:nowIso(),resultUrl:result.resultUrl,sourceUrl:result.resultUrl,transientResult:result.resultUrl.startsWith('blob:'),prompt:snapshot.prompt,negative_prompt:'',seedUsed:'',mediaType:'image',requestSnapshot:snapshot,estimatedCredits:snapshot.quote?.status==='estimate'?snapshot.quote.perOutput:null,settings:sanitizeHistoryItem({settings:{...normalized,width,height}}).settings}));
          setCurrent(items[0]);setVariations(items);setVariationStatus(Object.fromEntries(items.map(item=>[item.id,'loading'])));
          setHistory(prev=>[...items,...prev].slice(0,normalized.historyLimit));setActiveTab('prompt');setPreviewLoadState({status:'loading',error:''});
          if(response.warning)setGenerationNotice(response.warning);
        } finally { if(imageRequestRef.current===controller)imageRequestRef.current=null; }
        return;
      }

      if (normalized.mediaType === 'video') {
        const outs = selectedModelMeta?.output_modalities;
        const isVideoModel = Array.isArray(outs) && outs.includes('video');
        if (!selectedModelMeta) {
          setError(t('validation.videoModelsLoading'));
          return;
        }
        if (!isVideoModel) {
          setError(t('errors.selectedModelNotVideo'));
          return;
        }
      }

      if (normalized.mediaType === 'text' && !selectedModelMeta) {
        setError(t('errors.textModelsLoading'));
        return;
      }

      if (normalized.mediaType === 'audio' && !selectedModelMeta) {
        setError(t('errors.audioModelsLoading'));
        return;
      }

      if (normalized.mediaType === 'text') {
        const seedUsed = resolveSeedToUse(normalized) || '';
        const textPrompt = prompt; // reuse the composed prompt (brand + presets) as an instruction
        const textUrl = buildPollinationsTextUrl({
          prompt: textPrompt,
          model: normalized.model,
          seed: seedUsed || undefined,
          apiKey: normalized.useProxy ? undefined : apiKey || undefined,
          useProxy: normalized.useProxy,
        });
        const abs = new URL(textUrl, window.location.origin).toString();
        const resp = await fetch(abs);
        const out = await resp.text();
        if (!resp.ok) throw new Error(out || `HTTP ${resp.status}`);

        const item = {
          id: makeId(),
          createdAt: nowIso(),
          sourceUrl: textUrl,
          prompt: textPrompt,
          negative_prompt: '',
          seedUsed,
          mediaType: 'text',
          resultText: out,
          estimatedCredits: estimateCreditsForRun({
            pricing: selectedModelMeta?.pricing,
            mediaType: 'text',
            duration: normalized.duration,
          }),
          settings: { ...normalized, width, height },
        };
        setCurrent(item);
        setHistory((prev) => [item, ...prev].slice(0, normalized.historyLimit));
        setActiveTab('prompt');
        setPreviewLoadState({ status: 'loaded', error: '' });
        return;
      }

      if (normalized.mediaType === 'audio') {
        if (normalized.audioOperation === 'transcription') {
          if (!audioFile) throw new Error('Choose an audio file to transcribe.');
          if (audioFile.size > 25 * 1024 * 1024) throw new Error('Audio file must be 25 MB or smaller.');
          if (!normalized.useProxy) throw new Error('Transcription requires server proxy mode so the API key stays private.');
          const form = new FormData();
          form.set('file', audioFile);
          form.set('model', normalized.model);
          form.set('response_format', normalized.transcriptionFormat || 'json');
          if (normalized.transcriptionLanguage) form.set('language', normalized.transcriptionLanguage);
          const resp = await fetch('/api/audio?action=transcribe', { method: 'POST', body: form });
          const raw = await resp.text();
          if (!resp.ok) throw new Error(raw || `Transcription failed (HTTP ${resp.status})`);
          let resultText = raw;
          try { resultText = JSON.parse(raw)?.text || raw; } catch { /* plain text response */ }
          const item = { id: makeId(), createdAt: nowIso(), sourceUrl: '', prompt: audioFile.name, negative_prompt: '', seedUsed: '', mediaType: 'text', audioOperation: 'transcription', resultText, settings: { ...normalized } };
          setCurrent(item);
          setHistory((prev) => [item, ...prev].slice(0, normalized.historyLimit));
          setPreviewLoadState({ status: 'loaded', error: '' });
          return;
        }
        const textToSpeak = subject;
        const audioUrl = buildPollinationsAudioUrl({
          text: textToSpeak,
          model: normalized.model,
          voice: normalized.voice,
          responseFormat: normalized.audioFormat,
          duration: normalized.audioDuration,
          seconds: normalized.audioDuration,
          instrumental: normalized.instrumental,
          style: normalized.audioStyle,
          instruct: normalized.audioInstruct,
          loop: normalized.audioLoop,
          promptInfluence: normalized.promptInfluence,
          apiKey: normalized.useProxy ? undefined : apiKey || undefined,
          useProxy: normalized.useProxy,
        });

        const absoluteAudioUrl = new URL(audioUrl, window.location.origin).toString();
        const response = await fetch(absoluteAudioUrl);
        if (!response.ok) {
          const message = await response.text().catch(() => '');
          throw new Error(message || `Audio generation failed (HTTP ${response.status})`);
        }
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.startsWith('audio/')) throw new Error('Audio provider returned an unexpected response.');
        const blobUrl = URL.createObjectURL(await response.blob());
        const item = {
          id: makeId(),
          createdAt: nowIso(),
          sourceUrl: blobUrl,
          resultUrl: blobUrl,
          transientResult: true,
          prompt: textToSpeak,
          negative_prompt: '',
          seedUsed: '',
          mediaType: 'audio',
          audioOperation: normalized.audioOperation || 'speech',
          audioFormat: audioExtension(normalized.audioFormat, normalized.model),
          estimatedCredits: estimateCreditsForRun({
            pricing: selectedModelMeta?.pricing,
            mediaType: 'audio',
            duration: normalized.audioDuration,
          }),
          settings: { ...normalized, width, height },
        };
        setCurrent(item);
        setHistory((prev) => [item, ...prev].slice(0, normalized.historyLimit));
        setActiveTab('prompt');
        setPreviewLoadState({ status: 'loading', error: '' });
        return;
      }

      const maxVariations = normalized.mediaType === 'video' ? 2 : 6;
      const count = clampNumber(normalized.variations, { min: 1, max: maxVariations });
      const nextVariations = [];
      const nextStatus = {};
      let baseSeed = resolveSeedToUse(normalized);
      if (normalized.seedMode === 'custom' && !baseSeed) {
        baseSeed = String(Math.floor(Math.random() * 2147483647));
      }
      for (let i = 0; i < count; i++) {
        const seedUsed =
          normalized.seedMode === 'custom'
            ? baseSeed
            : String((Number(baseSeed) + i * 1337) % 2147483647);
        const referenceImage =
          false && normalized.referenceMode === 'strong'
            ? normalized.referenceImageDataUrl || undefined
            : normalized.logoInScene && !normalized.useProxy
              ? activeBrand?.logoDataUrlSmall || activeBrand?.logoDataUrl || undefined
              : undefined;
         const sourceUrl =
           normalized.mediaType === 'video'
             ? buildPollinationsVideoUrl({
                 prompt,
                 model: normalized.model,
                 seed: seedUsed || undefined,
                 apiKey: normalized.useProxy ? undefined : apiKey || undefined,
                 duration: normalized.duration,
                 aspectRatio: normalized.aspectRatio,
                 audio: normalized.audio,
                 referenceImage,
                 useProxy: normalized.useProxy,
               })
             : buildPollinationsImageUrl({
                 prompt,
                 model: normalized.model,
                 width,
                 height,
                 seed: seedUsed || undefined,
                 enhance: normalized.enhance,
                 negative_prompt,
                 safe: normalized.safe,
                 quality: normalized.quality,
                 transparent: normalized.transparent,
                 apiKey: normalized.useProxy ? undefined : apiKey || undefined,
                 referenceImage,
                 useProxy: normalized.useProxy,
               });
        const id = makeId();
        nextVariations.push({ id, seedUsed: seedUsed || '', sourceUrl, mediaType: normalized.mediaType });
        nextStatus[id] = 'loading';
      }

      for (const variation of nextVariations) {
        const response=await fetch(new URL(variation.sourceUrl,window.location.origin));
        if(!response.ok)throw new Error(`Video request failed (${response.status}). Review usage before trying again.`);
        if(!(response.headers.get('content-type') || '').startsWith('video/'))throw new Error('No saved video returned.');
        variation.sourceUrl=URL.createObjectURL(await response.blob());variation.resultUrl=variation.sourceUrl;variation.transientResult=true;
      }
      setVariations(nextVariations);
      setVariationStatus(nextStatus);

      // Pick the first variation as current and add to history.
      const first = nextVariations[0];
      const item = {
        id: makeId(),
        createdAt: nowIso(),
        sourceUrl: first.sourceUrl,
        resultUrl: first.resultUrl,
        transientResult: first.transientResult,
        prompt,
        negative_prompt,
        seedUsed: first.seedUsed,
        mediaType: normalized.mediaType,
        estimatedCredits: estimateCreditsForRun({
          pricing: selectedModelMeta?.pricing,
          mediaType: normalized.mediaType,
          duration: normalized.duration,
        }),
        settings: { ...normalized, width, height },
      };
      setCurrent(item);
      setPreviewNonce((n) => n + 1);
      setHistory((prev) => [item, ...prev].slice(0, normalized.historyLimit));
      setActiveTab('prompt');
      setPreviewLoadState({ status: 'loading', error: '' });
    } catch (e) {
      if (e?.code === 'CUSTOM_SEED_INVALID' || String(e?.message || '').includes('CUSTOM_SEED_INVALID')) {
        setError(t('errors.customSeedInvalid'));
      } else {
        setError(String(e));
      }
    } finally {
      setIsGenerating(false);
    }
  }

  async function onDownload() {
    if (!savedResultUrl(current) && current?.mediaType !== 'text') return;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const mt = current.mediaType || settings.mediaType;

    const brandIdForItem = current?.settings?.brandId || settings.brandId || activeBrandId || '';
    const brandForItem = (brands || []).find((b) => b?.id === brandIdForItem) || activeBrand;

    const overlayEnabled = Boolean(current?.settings?.logoOverlayEnabled ?? settings.logoOverlayEnabled);
    const overlayPosition = current?.settings?.logoOverlayPosition ?? settings.logoOverlayPosition;
    const overlaySizePct = current?.settings?.logoOverlaySizePct ?? settings.logoOverlaySizePct;
    const overlayOpacity = current?.settings?.logoOverlayOpacity ?? settings.logoOverlayOpacity;
    const overlayPadding = current?.settings?.logoOverlayPadding ?? settings.logoOverlayPadding;

    if (mt === 'image' && overlayEnabled && brandForItem?.logoDataUrl) {
      const url = savedResultUrl(current);
      const res = await downloadImageWithLogoOverlay({
        imageUrl: url,
        logoDataUrl: brandForItem.logoDataUrl,
        filename: `brand-studio-${stamp}.png`,
        position: overlayPosition,
        sizePct: overlaySizePct,
        opacity: overlayOpacity,
        padding: overlayPadding,
      });
      if (!res.ok) {
        setError(String(res.error || 'Logo overlay failed'));
        await downloadImageFromUrl(url, `brand-studio-${stamp}.jpg`);
      }
      return;
    }

    const ext = mt === 'video' ? 'mp4' : mt === 'audio' ? (current.audioFormat || 'mp3') : mt === 'text' ? 'txt' : 'jpg';
    await downloadImageFromUrl(savedResultUrl(current), `brand-studio-${stamp}.${ext}`);
  }

  async function onDownloadAllAsZip() {
    const mt = current?.mediaType || settings.mediaType;
    if (mt !== 'image') return;

    const bySource = new Map();
    (variations || []).forEach((v, idx) => {
      const src = savedResultUrl(v);
      if (!src) return;
      bySource.set(src, {
        sourceUrl: src,
        seedUsed: String(v?.seedUsed || '').trim(),
        index: idx + 1,
      });
    });
    const curSrc = savedResultUrl(current);
    if (curSrc && !bySource.has(curSrc)) {
      bySource.set(curSrc, {
        sourceUrl: curSrc,
        seedUsed: String(current?.seedUsed || '').trim(),
        index: bySource.size + 1,
      });
    }

    const items = Array.from(bySource.values());
    if (!items.length) return;

    try {
      setError('');
      const zip = new JSZip();
      await Promise.all(
        items.map(async (item) => {
          const src = item.sourceUrl;
          const res = await fetch(src, { mode: 'cors' });
          if (!res.ok) throw new Error(`Failed image download (HTTP ${res.status})`);
          const blob = await res.blob();
          const ext = String(blob.type || '').includes('png') ? 'png' : 'jpg';
          const seedPart = item.seedUsed ? `seed-${item.seedUsed}` : `image-${item.index}`;
          zip.file(`brand-studio-${seedPart}.${ext}`, blob);
        }),
      );

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      const url = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `brand-studio-images-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2500);
    } catch (e) {
      setError(`Download all failed: ${String(e?.message || e)}`);
    }
  }

  function applySettingsPatch(patch) {
    const patchObj = patch && typeof patch === 'object' ? patch : {};
    setSettings((prev) => normalizeSettings({ ...prev, ...patchObj }));

    const id = current?.id || '';
    if (!id) return;

    setCurrent((prev) => {
      if (!prev) return prev;
      return { ...prev, settings: normalizeSettings({ ...(prev.settings || {}), ...patchObj }) };
    });

    setHistory((prev) =>
      (prev || []).map((item) =>
        item?.id === id ? { ...item, settings: normalizeSettings({ ...(item.settings || {}), ...patchObj }) } : item,
      ),
    );
  }

  function onCopy(text) {
    if (!text) return;
    navigator.clipboard?.writeText(text);
  }

  function lockSeedFromCurrent() {
    const seed = String(current?.seedUsed || '').trim();
    if (!seed) return;
    updateSetting('seedMode', 'custom');
    updateSetting('seedValue', seed);
  }

  function pickVariation(v) {
    if (!savedResultUrl(v)) return;
    if (v.requestSnapshot) { setCurrent(v); return; }
    const mt = v.mediaType || settings.mediaType;
    const item = {
      id: makeId(),
      createdAt: nowIso(),
      sourceUrl: v.sourceUrl,
      resultUrl: v.resultUrl,
      transientResult: v.transientResult,
      prompt: current?.prompt || promptPreview.prompt,
      negative_prompt: current?.negative_prompt || promptPreview.negative_prompt,
      seedUsed: v.seedUsed || '',
      mediaType: mt,
      estimatedCredits: estimateCreditsForRun({
        pricing: selectedModelMeta?.pricing,
        mediaType: mt,
        duration: Number(current?.settings?.duration ?? settings.duration),
      }),
      settings: current?.settings || normalizeSettings(settings),
    };
    setCurrent(item);
    setHistory((prev) => [item, ...prev].slice(0, (current?.settings?.historyLimit || settings.historyLimit)));
  }

  function onRetryPreview() {
    if (current?.mediaType === 'text') {
      onGenerate();
      return;
    }
    setPreviewLoadState({ status: 'loading', error: '' });
    setPreviewNonce((n) => n + 1);
  }

  function buildDirectUrlFromProxyUrl(proxyUrl, mediaType) {
    const raw = String(proxyUrl || '').trim();
    if (!raw) return '';
    let u;
    try {
      u = new URL(raw, window.location.origin);
    } catch {
      return '';
    }
    const path = u.pathname || '';
    const mt = String(mediaType || '').trim() || 'image';

    if (path.endsWith('/api/image')) {
      const prompt = String(u.searchParams.get('prompt') || '').trim();
      if (!prompt) return '';
      return buildPollinationsImageUrl({
        prompt,
        model: u.searchParams.get('model') || undefined,
        width: u.searchParams.get('width') || undefined,
        height: u.searchParams.get('height') || undefined,
        seed: u.searchParams.get('seed') || undefined,
        enhance: u.searchParams.get('enhance') || undefined,
        negative_prompt: u.searchParams.get('negative_prompt') || undefined,
        safe: u.searchParams.get('safe') || undefined,
        quality: u.searchParams.get('quality') || undefined,
        transparent: u.searchParams.get('transparent') || undefined,
        referenceImage: u.searchParams.get('image') || undefined,
        apiKey: undefined,
        useProxy: false,
      });
    }

    if (path.endsWith('/api/video') || (mt === 'video' && raw.startsWith('/api/video'))) {
      const prompt = String(u.searchParams.get('prompt') || '').trim();
      if (!prompt) return '';
      return buildPollinationsVideoUrl({
        prompt,
        model: u.searchParams.get('model') || undefined,
        seed: u.searchParams.get('seed') || undefined,
        duration: u.searchParams.get('duration') || undefined,
        aspectRatio: u.searchParams.get('aspectRatio') || u.searchParams.get('aspect-ratio') || undefined,
        audio: u.searchParams.get('audio') || undefined,
        referenceImage: u.searchParams.get('image') || undefined,
        apiKey: undefined,
        useProxy: false,
      });
    }

    return '';
  }

  function maybeFallbackFromProxyForCurrent() {
    const cur = current;
    if (!cur?.sourceUrl) return false;
    if (!settings.useProxy) return false;
    if (!String(cur.sourceUrl).startsWith('/api/')) return false;
    if (!apiKey) return false;

    const id = String(cur.id || '').trim();
    if (!id) return false;
    if (proxyFallbackTriedRef.current.has(id)) return false;

    const direct = buildDirectUrlFromProxyUrl(cur.sourceUrl, cur.mediaType);
    if (!direct) return false;

    proxyFallbackTriedRef.current.add(id);

    setCurrent((prev) => (prev ? { ...prev, sourceUrl: direct } : prev));
    setVariations((prev) =>
      (prev || []).map((v) => {
        const src = String(v?.sourceUrl || '');
        if (!src.startsWith('/api/')) return v;
        const next = buildDirectUrlFromProxyUrl(src, v?.mediaType);
        return next ? { ...v, sourceUrl: next } : v;
      }),
    );
    setPreviewLoadState({ status: 'loading', error: '' });
    setPreviewNonce((n) => n + 1);
    return true;
  }

  function onClearHistory() {
    setHistory([]);
    setCurrent(null);
    clearHistory();
  }

  async function onUploadLogo(file) {
    if (!file || !activeBrand) return;
    try {
      const [imageDataLarge, imageDataSmall] = await Promise.all([
        loadImageToImageData(file, 512),
        loadImageToImageData(file, 256),
      ]);
      const palette = extractPaletteFromImageData(imageDataSmall, 5);
      const logoDataUrl = imageDataToPngDataUrl(imageDataLarge);
      const logoDataUrlSmall = imageDataToPngDataUrl(imageDataSmall);
      setBrands((prev) =>
        prev.map((b) =>
          b.id === activeBrand.id
            ? { ...b, logoName: file.name, palette, logoDataUrl, logoDataUrlSmall, updatedAt: new Date().toISOString() }
            : b,
        ),
      );
    } catch (e) {
      setError(`Logo upload failed: ${String(e)}`);
    }
  }

  function onClearLogo() {
    if (!activeBrand) return;
    setBrands((prev) =>
      prev.map((b) =>
        b.id === activeBrand.id
          ? { ...b, logoName: '', palette: [], logoDataUrl: '', logoDataUrlSmall: '', updatedAt: new Date().toISOString() }
          : b,
      ),
    );
    setSettings((prev) =>
      normalizeSettings({
        ...prev,
        logoInScene: false,
        logoOverlayEnabled: false,
      }),
    );
  }

  function updateActiveBrand(patch) {
    if (!activeBrand) return;
    setBrands((prev) =>
      prev.map((b) =>
        b.id === activeBrand.id ? { ...b, ...patch, updatedAt: new Date().toISOString() } : b,
      ),
    );
  }

  function createBrand() {
    const b = makeDefaultBrandProfile();
    setBrands((prev) => [b, ...prev]);
    setActiveBrandId(b.id);
    setSettings((prev) => ({ ...prev, brandId: b.id, useBrandProfile: true }));
  }

  function applyBrandDefaults() {
    if (!activeBrand?.defaults) return;
    const d = activeBrand.defaults;
    setSettings((prev) => {
      const next = {
        ...prev,
        platformPreset: d.platformPreset ?? prev.platformPreset,
        stylePreset: d.stylePreset ?? prev.stylePreset,
        lightingPreset: d.lightingPreset ?? prev.lightingPreset,
        compositionPreset: d.compositionPreset ?? prev.compositionPreset,
        palettePreset: d.palettePreset ?? prev.palettePreset,
        moodPreset: d.moodPreset ?? prev.moodPreset,
        allowText: d.allowText ?? prev.allowText,
        avoidLogos: d.avoidLogos ?? prev.avoidLogos,
      };
      const preset = PLATFORM_PRESETS.find((p) => p.value === next.platformPreset);
      if (preset && preset.value !== 'custom') {
        next.width = preset.width;
        next.height = preset.height;
      }
      return normalizeSettings(next);
    });
  }

  const schemaWithDynamicModels = useMemo(() => {
    const type = settings.mediaType || 'image';

    const catalog =
      type === 'text'
        ? textModels
        : type === 'audio'
          ? modelsForAudioOperation(audioModels, settings.audioOperation || 'speech')
          : imageModels.filter((m) => {
              if (!m || typeof m.name !== 'string') return false;
              const outs = m.output_modalities;
              const isVideo = Array.isArray(outs) && outs.includes('video');
              return type === 'video' ? isVideo : !isVideo;
            });

    const modelOptions = (catalog || [])
      .filter((m) => m && typeof m.name === 'string')
      .map((m) => {
        const p = m?.pricing || {};
        const cost =
          type === 'video'
            ? typeof p.completionVideoSeconds === 'number'
              ? `${p.completionVideoSeconds} credits/sec`
              : typeof p.completionVideoTokens === 'number'
                ? `${p.completionVideoTokens} credits/video-token`
                : ''
            : type === 'text'
              ? typeof p.completionTextTokens === 'number'
                ? `${p.completionTextTokens} credits/token`
                : ''
              : type === 'audio'
                ? typeof p.completionAudioTokens === 'number'
                  ? `${p.completionAudioTokens} credits/audio-token`
                  : ''
                : typeof p.completionImageTokens === 'number'
                  ? quoteLabel(quoteGeneration({outputCount:1},m))
                  : '';

        const flags = [m.paid_only ? 'paid' : '', m.description?.includes('ALPHA') ? 'alpha' : '']
          .filter(Boolean)
          .join(' • ');
        const suffix = [cost, flags].filter(Boolean).join(' • ');
        return {
          value: m.name,
          label: suffix ? `${m.name} — ${suffix}` : m.name,
        };
      });

    const optionsWithLoading =
      modelOptions.length === 0 ? [{ value: '', label: `Loading ${type} models…`, disabled: true }] : modelOptions;

    const voiceOptions =
      type === 'audio' && Array.isArray(selectedModelMeta?.voices)
        ? selectedModelMeta.voices.map((v) => ({ value: v, label: v }))
        : null;

    return SETTINGS_SCHEMA.filter(section => !['creative','reference'].includes(section.id)).map((section) => ({
      ...section,
      fields: section.fields.filter(f => !['promptAssist','logoInScene','brandConsistency','allowText','avoidLogos','useBrandProfile'].includes(f.key) && !(type === 'image' && ['seedMode','seedValue','enhance','safe','transparent','useProxy'].includes(f.key))).map((f) => {
        if (f.key === 'model') return { ...f, options: optionsWithLoading.length ? optionsWithLoading : f.options };
        if (f.key === 'voice' && voiceOptions) return { ...f, options: voiceOptions };
        if (f.key === 'voice' && type === 'audio') return { ...f, visibleIf: { key: 'audioOperation', equals: 'speech' } };
        if (f.key === 'audio' && type === 'video') {
          const supported = Array.isArray(selectedModelMeta?.video_capabilities) && selectedModelMeta.video_capabilities.includes('audio_output');
          return { ...f, disabled: !supported, help: supported ? 'This model supports generated audio.' : 'The selected model does not advertise audio output.' };
        }
        return f;
      }),
    }));
  }, [imageModels, textModels, audioModels, settings.mediaType, settings.audioOperation, selectedModelMeta]);

  const settingsContent = (
    <>
      <div className="SettingsBlock">
        <div className="SettingsBlockTitle">{t('settings.blockBrand')}</div>
        <div className="Row Row--tight" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div className="Hint">{activeBrand?.name ? `Brand: ${activeBrand.name}` : 'Brand'}</div>
          <button
            className="Btn Btn--ghost"
            type="button"
            onClick={() => setActivePage('brandKit')}
            title="Edit brand identity in Brand Kit"
          >
            {t('common.edit')} {t('nav.brandKit')}
          </button>
        </div>
        <div className="Row Row--tight" style={{ marginTop: 10, gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <label className="Field" style={{ flex: '1 1 220px', margin: 0 }}>
            <div className="Field-label">{t('brand.profile')}</div>
            <select
              className="SelectSlim"
              value={activeBrandId || ''}
              onChange={(e) => {
                const id = e.target.value;
                setActiveBrandId(id);
                setSettings((prev) => ({ ...prev, brandId: id, useBrandProfile: true }));
              }}
            >
              {(brands || []).map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name || b.id}
                </option>
              ))}
            </select>
          </label>

          <button className="Btn Btn--ghost" type="button" onClick={createBrand}>
            {t('brand.new')}
          </button>

          <button className="Btn" type="button" onClick={applyBrandDefaults} disabled={!activeBrand?.defaults}>
            {t('brand.applyDefaults')}
          </button>
        </div>
        <div className="InlineHint" style={{ marginTop: 10 }}>
          Keep your logo, colors, roles, and IG templates inside Brand Kit. Use “Add brand identity” to insert brand instructions into your prompt.
        </div>
      </div>

      <div className="SettingsBlock">
        <div className="SettingsBlockTitle">{t('settings.blockGeneration')}</div>
        {modelsError ? <div className="InlineWarn">{t('settings.modelsPrefix', { msg: modelsError })}</div> : null}
        {settings.mediaType === 'video' ? (
          <div className="InlineWarn">
            {t('settings.videoPaidOnlyNote')}
          </div>
        ) : null}
        <div className="CardFlat" style={{ marginBottom: 10 }}>
          <div className="Row Row--tight" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div className="Hint">{t('settings.usageTitle')}</div>
            <div className="Hint">
              {[modelCostLabel, modelDailyEstimate].filter(Boolean).join(' • ')}
            </div>
          </div>
          {modelRunEstimate ? <div className="InlineHint">{t('settings.usagePerRun', { estimate: modelRunEstimate })}</div> : null}
          {totalEstimatedCredits ? (
            <div className="InlineHint" style={{ marginTop: 6 }}>
              {t('settings.usageTotal', { credits: formatCredits(totalEstimatedCredits) })}
            </div>
          ) : null}
          <div className="InlineHint" style={{ marginTop: 6 }}>
            {t('settings.usageDisclaimer')}
          </div>
        </div>
        <SettingsForm schema={schemaWithDynamicModels} settings={settings} onChange={updateSetting} mobile={mobile} />
        {settings.mediaType === 'video' ? <p className="InlineHint">Video references are a separate upcoming stage. Image attachments apply only to image generation.</p> : null}
        {modelCostLabel || modelDailyEstimate ? (
          null
        ) : null}
      </div>
    </>
  );

  const workspaceHero = useMemo(() => {
    if (activePage === 'brandKit') {
      return {
        kicker: t('nav.brandKit'),
        titleA: 'Live brand',
        titleB: 'editing',
        body:
          'Tap any avatar, highlight, or post to edit it directly. Keep the brand palette, logo, and AI image generation in one live Instagram mock.',
        stats: [
          { label: 'Brand', value: activeBrand?.name || t('brand.untitled') },
          { label: 'Palette', value: String((activeBrand?.palette || []).length || 0) },
          { label: 'Step', value: t('brandKit.steps.studio') },
        ],
      };
    }

    return {
      kicker: t('nav.studio'),
      titleA: 'Generate',
      titleB: 'everything',
      body:
        'A focused creation workspace for images, video, audio, and text. Brand controls stay with the brand, while preview and settings stay side by side.',
      stats: [
        { label: 'Brand', value: activeBrand?.name || t('brand.untitled') },
        { label: 'Mode', value: settings.useProxy ? t('settings.apiKeyStatusProxy') : inferredMediaType },
        { label: 'History', value: String(history.length || 0) },
      ],
    };
  }, [activePage, activeBrand?.name, activeBrand?.palette, history.length, inferredMediaType, settings.useProxy, t]);

  return (
    <div className={`Shell ${activePage === 'studio' ? 'Shell--studio' : ''}`}>
      <header className="Header">
        <button
          type="button"
          className={`HeaderLeft HeaderLeftBtn ${activePage === 'studio' ? '' : 'HeaderLeftBtn--disabled'}`}
          onClick={() => {
            setActivePage('studio');
          }}
          aria-label={t('nav.studio')}
        >
          <div className="AppMark" aria-hidden="true">
            <img className="AppMarkImg" src={`${process.env.PUBLIC_URL}/codefalcon-mark.png`} alt="" />
          </div>
          <div>
            <div className="AppTitle">{t('app.title')}</div>
            <div className="AppSubtitle">BY CODEFALCON</div>
          </div>
        </button>

        <div className="HeaderRight">
          <div className="Seg" role="tablist" aria-label={t('nav.pages')}>
            <button
              type="button"
              className={`SegBtn ${activePage === 'studio' ? 'SegBtn--active' : ''}`}
              onClick={() => setActivePage('studio')}
            >
              {t('nav.studio')}
            </button>
            <button
              type="button"
              className={`SegBtn ${activePage === 'brandKit' ? 'SegBtn--active' : ''}`}
              onClick={() => setActivePage('brandKit')}
            >
              {t('nav.brandKit')}
            </button>
            <button type="button" className={`SegBtn ${activePage === 'library' ? 'SegBtn--active' : ''}`} onClick={() => setActivePage('library')}>{locale === 'ar' ? 'المكتبة' : 'Library'}</button>
          </div>

          <div className="Seg" role="tablist" aria-label={t('common.language')}>
            <button type="button" className={`SegBtn ${locale === 'en' ? 'SegBtn--active' : ''}`} onClick={() => setLocale('en')}>
              EN
            </button>
            <button type="button" className={`SegBtn ${locale === 'ar' ? 'SegBtn--active' : ''}`} onClick={() => setLocale('ar')}>
              عربي
            </button>
          </div>

          <button
            type="button"
            className={`ApiStatusBtn ${apiConnected ? 'ApiStatusBtn--ok' : 'ApiStatusBtn--warn'}`}
            onClick={() => setApiKeyDialogOpen(true)}
            aria-label={t('settings.apiKeyDialogTitle')}
            title={apiConnected ? t('settings.apiKeyDialogConnected') : t('settings.apiKeyDialogNotConnected')}
          >
            <span className="ApiStatusDot" aria-hidden="true" />
            <span className="ApiStatusText">
              {apiConnected ? t('settings.apiKeyDialogConnected') : `${t('settings.apiKeyDialogNotConnected')} • ${t('common.edit')}`}
            </span>
          </button>

          <button
            className="HeaderSettings Btn Btn--ghost"
            type="button"
            onClick={() => setSettingsOpen(true)}
            hidden={activePage !== 'studio'}
          >
            <StudioIcon name="settings" size={17} /> {t('common.settings')}
          </button>

          <button
            className="IconBtn"
            type="button"
            onClick={() => setTheme((prev) => (prev === 'light' ? 'dark' : 'light'))}
            aria-label={t('common.toggleTheme')}
            title={t('common.toggleTheme')}
          >
            {theme === 'light' ? '🌙' : '☀'}
          </button>
        </div>
      </header>

      {error ? (
        <div className="Toast" role="status">
          {error}
        </div>
      ) : null}

      {activePage === 'brandKit' ? <section className="Hero">
        <div className="HeroInner">
          <div className="HeroCopy">
            <div className="HeroKicker">{workspaceHero.kicker}</div>
            <h1 className="HeroTitle">
              <span className="HeroTitleA">{workspaceHero.titleA}</span>{' '}
              <span className="HeroTitleB">{workspaceHero.titleB}</span>
            </h1>
            <div className="HeroBody">{workspaceHero.body}</div>
          </div>
          <div className="HeroStats" aria-label={t('nav.pages')}>
            {workspaceHero.stats.map((item) => (
              <div key={item.label} className="HeroStat">
                <div className="HeroStatLabel">{item.label}</div>
                <div className="HeroStatValue">{item.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section> : null}

      <main className="Main">
        {activePage === 'studio' ? <section className="WorkspaceIntro">
          <div>
            <div className="WorkspaceEyebrow"><span /> {t('studio.eyebrow')}</div>
            <h1>{t('studio.title')} <em>{t('studio.titleAccent')}</em></h1>
            <p>{t('studio.description')}</p>
          </div>
          <button className="WorkspaceBrand" type="button" onClick={() => setActivePage('brandKit')}>
            <span className="WorkspaceBrandMark">{activeBrand?.logoDataUrlSmall || activeBrand?.logoDataUrl ? <img src={activeBrand.logoDataUrlSmall || activeBrand.logoDataUrl} alt="" /> : <StudioIcon />}</span>
            <span><small>{t('studio.activeBrand')}</small><strong>{activeBrand?.name || t('brand.untitled')}</strong></span>
            <span className="WorkspacePalette" aria-hidden="true">{(activeBrand?.palette || []).slice(0, 3).map((color, index) => <i key={index} style={{ background: color }} />)}</span>
            <StudioIcon name="arrow" size={16} />
          </button>
        </section> : null}
        <div className="MainLeft">
          {activePage === 'library' ? <LibraryPage locale={locale} pendingResult={libraryPending} onSaved={() => setLibraryPending(null)} /> : activePage === 'brandKit' ? (
            <BrandKitPage
              brands={brands}
              activeBrand={activeBrand}
              activeBrandId={activeBrandId}
              onSetActiveBrandId={(id) => {
                setActiveBrandId(id);
                setSettings((prev) => ({ ...prev, brandId: id, useBrandProfile: true }));
              }}
              onCreateBrand={createBrand}
              onUpdateActiveBrand={updateActiveBrand}
              onUploadLogo={onUploadLogo}
              onClearLogo={onClearLogo}
              apiKey={apiKey}
              useProxy={Boolean(settings.useProxy)}
            />
          ) : (
            <div className={`StudioGrid ${settingsOpen ? '' : 'StudioGrid--settingsClosed'}`}>
              <div className="StudioCol StudioCol--left">
                <PromptCard
                  mediaType={settings.mediaType}
                  subject={settings.subject}
                  onChangeSubject={(v) => updateSetting('subject', v)}
                  onChangeMediaType={(v) => updateSetting('mediaType', v)}
                  onGenerate={onGenerate}
                  isGenerating={isCreating}
                  lastError={error}
                  onOpenSettings={() => setSettingsOpen(true)}
                  showPreviewButton={false}
                  onOpenPreview={() => {}}
                  previewDisabled
                  settings={settings}
                  audioModels={audioModels}
                  onChangeAudioSetting={updateSetting}
                  audioFile={audioFile}
                  onAudioFileChange={setAudioFile}
                  modelLabel={settings.model}
                  brandName={activeBrand?.name || 'Brand'}
                  brandProfile={activeBrand}
                  generationBlocked={Boolean(imageIssue)}
                  requestPanel={settings.mediaType === 'image' ? <ImageRequestPanel models={imageModels} modelId={settings.model} onModel={v=>updateSetting('model',v)} onRefresh={async()=>{try{const models=await fetchPollinationsImageModels();setImageModels(models);saveCachedModels('image',models);setError('');}catch(e){setError('Cannot refresh model prices. Try again later.');}}} file={referenceFile} preview={settings.referenceImageDataUrl} onFile={onUploadReference} onClear={clearReference} intent={referenceIntent} onIntent={setReferenceIntent} quote={imageQuote} issue={imageIssue} size={imageDraft.size} count={imageDraft.outputCount} busy={isCreating}/> : null}
                  formatLabel={settings.mediaType === 'audio' ? settings.audioFormat : settings.mediaType === 'video' ? settings.aspectRatio : settings.mediaType === 'image' ? `${settings.width}×${settings.height}` : 'Text'}
                />

                <TabsPanel
                  activeTab={activeTab}
                  onChangeTab={setActiveTab}
                  historyCount={history.length}
                  promptPreview={current ? {prompt:current.requestSnapshot?.prompt ?? current.prompt ?? '',negative_prompt:current.negative_prompt || ''} : promptPreview}
                  onCopy={onCopy}
                  history={history}
                  currentId={current?.id || ''}
                  onPickHistory={(item) => {if(isCreating)return;setError('');setCurrent(item);setVariations([]);}}
                  persistHistory={settings.persistHistory}
                  onClearHistory={onClearHistory}
                  apiKey={apiKey}
                  addKeyToUrl={addKeyToUrl}
                  variations={variations}
                  variationStatus={variationStatus}
                  onPickVariation={(v) => pickVariation(v)}
                  onVariationLoad={(id) => setVariationStatus((prev) => ({ ...prev, [id]: 'loaded' }))}
                  onVariationError={(id) => setVariationStatus((prev) => ({ ...prev, [id]: 'error' }))}
                  mediaType={inferredMediaType}
                />
              </div>

              <div className="StudioCol StudioCol--center">
                {generationNotice ? <p className="InlineWarn">{generationNotice}</p> : null}
                {needsReference ? <button className="Btn Btn--ghost" type="button" onClick={clearReference}>{locale === 'ar' ? 'ابدأ بدون مرجع' : 'Start without a reference'}</button> : null}
                {current && current.mediaType !== 'text' && !previewUrl ? <div className="CardFlat">
                  <p>{locale === 'ar' ? 'هذا السجل يحتفظ بالوصف فقط. النتيجة القديمة غير متاحة ولن نعيد توليدها تلقائياً.' : 'Only this request’s details are saved. Its old result is unavailable and will not be regenerated automatically.'}</p>
                  <button type="button" className="Btn" disabled={isCreating} onClick={() => {updateSetting('subject',current.prompt || '');setNeedsReference(Boolean(current.requestSnapshot?.referenceMetadata || current.settings?.referenceImageDataUrl));setError(current.requestSnapshot?.referenceMetadata || current.settings?.referenceImageDataUrl ? (locale === 'ar' ? 'أعد إرفاق الصورة المرجعية قبل التوليد.' : 'Reattach the reference image before generating.') : '');setCurrent(null);}}>{locale === 'ar' ? 'استخدم هذا الوصف' : 'Use this prompt'}</button>
                </div> : null}
                {!isCreating && ['image', 'video'].includes(current?.mediaType) && savedResultUrl(current) ? <button type="button" className="Btn" onClick={() => { setLibraryPending(current); setActivePage('library'); }}>{locale === 'ar' ? 'حفظ في المكتبة' : 'Save to library'}</button> : null}
                <PreviewHero key={`${current?.id || 'empty'}:${previewNonce}`}
                  previewUrl={previewUrl}
                  textOutput={current?.resultText || ''}
                  sizeLabel={
                    inferredMediaType === 'image'
                      ? (current?.requestSnapshot?.size?.replace('x', '×') || (current?.settings?.width && current?.settings?.height ? `${current.settings.width}×${current.settings.height}` : `${promptPreview.width}×${promptPreview.height}`))
                      : inferredMediaType === 'video'
                        ? `${settings.aspectRatio} • ${settings.duration}s`
                        : inferredMediaType === 'audio'
                          ? `${settings.audioOperation || 'speech'}${settings.audioOperation === 'speech' ? ` · ${settings.voice || '—'}` : ''}`
                          : ''
                  }
                  modelLabel={current?.requestSnapshot?.modelId || current?.settings?.model || settings.model}
                  seedLabel={current?.seedUsed ? t('variations.seedLabel', { seed: current.seedUsed }) : ''}
                  onLockSeed={lockSeedFromCurrent}
                  onCopyUrl={() => onCopy(previewUrl)}
                  onCopyText={() => onCopy(current?.resultText || '')}
                  onOpen={() => {
                    if (!previewUrl) return;
                    window.open(previewUrl, '_blank', 'noopener,noreferrer');
                  }}
                  onDownload={onDownload}
                  onDownloadAll={onDownloadAllAsZip}
                  onRetry={error ? (settings.mediaType === 'image' ? () => setError('') : onGenerate) : onRetryPreview}
                  errorActionLabel={error && settings.mediaType === 'image' ? (locale === 'ar' ? 'راجع الطلب' : 'Review request') : undefined}
                  canLockSeed={!isCreating && Boolean(current?.seedUsed)}
                  canDownload={!isCreating && !error && previewLoadState.status !== 'error' && Boolean(previewUrl || current?.resultText)}
                  canDownloadAll={!isCreating && inferredMediaType === 'image' && Boolean((variations && variations.length) || savedResultUrl(current))}
                  isLoading={isCreating}
                  errorMessage={error || (previewLoadState.status === 'error' ? previewLoadState.error : '')}
                  onImageLoad={() => { if (!isGenerating) setPreviewLoadState({ status: 'loaded', error: '' }); }}
                  onImageError={() => {
                    if (isGenerating) return;
                    if (!current?.resultUrl && maybeFallbackFromProxyForCurrent()) return;
                    setPreviewLoadState({
                      status: 'error',
                      error: locale === 'ar' ? 'تعذّر تحميل النتيجة المحفوظة أو انتهت صلاحيتها. تحديث العرض لا يولّد صورة جديدة.' : 'The saved result could not load or has expired. Retrying the preview does not generate a new image.',
                    });
                  }}
                  mediaType={isGenerating ? inferredMediaType : current?.mediaType || inferredMediaType}
                  brandName={activeBrand?.name || ''}
                  avatarDataUrl={activeBrand?.logoDataUrlSmall || activeBrand?.logoDataUrl || ''}
                  caption={current?.prompt || settings.subject || ''}
                  logoOverlay={(() => {
                    const brandIdForItem = current?.settings?.brandId || settings.brandId || activeBrandId || '';
                    const brandForItem = (brands || []).find((b) => b?.id === brandIdForItem) || activeBrand;
                    const overlayEnabled = Boolean(current?.settings?.logoOverlayEnabled ?? settings.logoOverlayEnabled);
                    return {
                      enabled: overlayEnabled && inferredMediaType === 'image',
                      logoDataUrl: brandForItem?.logoDataUrl || '',
                      position: current?.settings?.logoOverlayPosition ?? settings.logoOverlayPosition,
                      sizePct: current?.settings?.logoOverlaySizePct ?? settings.logoOverlaySizePct,
                      opacity: current?.settings?.logoOverlayOpacity ?? settings.logoOverlayOpacity,
                      padding: current?.settings?.logoOverlayPadding ?? settings.logoOverlayPadding,
                    };
                  })()}
                  onUpdateLogoOverlay={applySettingsPatch}
                />
              </div>

            </div>
          )}
        </div>
      </main>

      {activePage === 'studio' ? (
        <Sheet open={settingsOpen} title={t('settings.title')} onClose={() => setSettingsOpen(false)}>
          {settingsContent}
        </Sheet>
      ) : null}

      <ApiKeyDialog
        open={apiKeyDialogOpen}
        onClose={() => setApiKeyDialogOpen(false)}
        apiKeyInput={apiKeyInput}
        setApiKeyInput={setApiKeyInput}
        statusVariant={apiConnected ? 'connected' : 'warning'}
        statusText={apiStatusText}
        useProxy={Boolean(settings.useProxy)}
        devProxyHint={
          settings.useProxy && process.env.NODE_ENV === 'development'
            ? 'Proxy mode needs Cloudflare Pages Functions. In dev, use: npm run build then npx wrangler pages dev build.'
            : ''
        }
        onSave={() => {
          const next = String(apiKeyInput || '').trim();
          setRuntimeApiKey(next);
          saveRuntimeApiKey(next);
          setError('');
          setApiKeyDialogOpen(false);
        }}
        onClear={() => {
          setRuntimeApiKey('');
          setApiKeyInput('');
          clearRuntimeApiKey();
        }}
        canClear={Boolean(runtimeApiKey)}
      />

      <footer className="Footer">
        <div className="FooterInner">
          <div className="FooterText">{t('app.footer')}</div>
          <a className="FooterLink" href={companyUrl} target="_blank" rel="noreferrer">
            codefalcon.me
          </a>
        </div>
      </footer>
    </div>
  );
}
